/**
 * Aba Visão do Processo — VSM informativo; KPIs só com mapa válido.
 */
import React, { useMemo } from 'react';
import {
  AlertTriangle,
  ExternalLink,
  Flame,
  Map as MapIcon,
  Timer,
} from 'lucide-react';
import type { CardData, Action } from '../../types';
import type { ProjectPlansSummary } from '../../types/projectPlan';
import {
  ClickableMetric,
  ProjectEmptyState,
  SectionPanel,
  primaryButtonClassName,
  secondaryButtonClassName,
} from './projectHubShared';

interface ProjectProcessViewTabProps {
  projectId: string;
  summary: ProjectPlansSummary;
  cards: CardData[];
  actions: Action[];
  hasVsm?: boolean;
  onOpenVsm: () => void;
  onNavigateOverdueActions: () => void;
  onNavigateCriticalProblems: () => void;
  onNavigateActions: () => void;
  onNavigateExecutions: () => void;
}

function toMinutes(value: number | string | undefined): number {
  if (value == null) return 0;
  const n = typeof value === 'string' ? parseFloat(value) : value;
  return Number.isFinite(n) ? n : 0;
}

function estimateFlowMetrics(cards: CardData[], actions: Action[]) {
  const totalTp = cards.reduce((sum, card) => sum + toMinutes(card.tp ?? card.lt), 0);
  const totalTra = cards.reduce((sum, card) => sum + toMinutes(card.tpEfetivo ?? card.tp), 0);
  const leadTime = totalTp > 0 ? totalTp : cards.length * 8;
  const pce = leadTime > 0 ? Math.round((totalTra / leadTime) * 100) : 0;
  const wip = actions.filter(a => a.status !== 'done' && a.status !== 'canceled').length;
  const slaRisk = actions.filter(a => {
    const due = a.prazo ?? a.deadline;
    if (!due || a.status === 'done') return false;
    return new Date(due).getTime() < Date.now();
  }).length;
  return { leadTime, pce, wip, slaRisk };
}

export const ProjectProcessViewTab: React.FC<ProjectProcessViewTabProps> = ({
  projectId,
  summary,
  cards,
  actions,
  hasVsm: hasVsmProp,
  onOpenVsm,
  onNavigateOverdueActions,
  onNavigateCriticalProblems,
  onNavigateActions,
  onNavigateExecutions,
}) => {
  const hasVsm = hasVsmProp ?? cards.length > 0;
  const flow = useMemo(() => estimateFlowMetrics(cards, actions), [cards, actions]);

  const executors = useMemo(() => {
    const names = new Set<string>();
    actions.forEach(a => {
      const resp = a.responsavel ?? a.responsible;
      if (resp) names.add(resp);
    });
    return names.size;
  }, [actions]);

  const bottlenecks = useMemo(() => {
    return [...cards]
      .map(card => ({
        card,
        wait: Math.max(0, toMinutes(card.tp ?? card.lt) - toMinutes(card.tpEfetivo ?? card.tp)),
      }))
      .sort((a, b) => b.wait - a.wait)
      .slice(0, 3)
      .filter(item => item.wait > 0);
  }, [cards]);

  if (!hasVsm) {
    return (
      <div className="p-2">
        <ProjectEmptyState
          icon={<MapIcon size={24} />}
          title="Fluxo ainda não estruturado"
          description="Nenhum processo modelado neste projeto. Sem mapa VSM não há KPIs de fluxo, gargalos ou diagnóstico operacional disponíveis."
          actions={
            <button type="button" onClick={onOpenVsm} className={primaryButtonClassName()}>
              <MapIcon size={12} />
              Criar Fluxo
            </button>
          }
        />
        <ul className="mx-auto mt-3 max-w-md space-y-1 text-center text-[10px] text-slate-500">
          <li>Nenhum processo modelado</li>
          <li>Nenhum KPI disponível</li>
          <li>Nenhum gargalo identificado</li>
        </ul>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-2 p-2">
      <div className="grid grid-cols-2 gap-1.5 md:grid-cols-4">
        <ClickableMetric label="Lead Time" value={`${flow.leadTime.toFixed(1)} min`} onClick={onOpenVsm} />
        <ClickableMetric label="PCE" value={`${flow.pce}%`} onClick={onOpenVsm} tone={flow.pce >= 60 ? 'success' : 'warning'} />
        <ClickableMetric label="SLA em risco" value={flow.slaRisk} tone={flow.slaRisk > 0 ? 'danger' : 'default'} onClick={onNavigateOverdueActions} />
        <ClickableMetric label="WIP" value={flow.wip} onClick={onNavigateActions} />
      </div>

      <div className="grid gap-2 lg:grid-cols-12">
        <div className="lg:col-span-7">
          <SectionPanel title="Mapa do Processo" icon={<MapIcon size={12} className="text-blue-500" />}>
            <div className="overflow-x-auto rounded border border-slate-100 bg-slate-50 p-2 dark:border-slate-800 dark:bg-slate-950/50">
              <div className="flex min-w-max items-center gap-1.5">
                {cards.slice(0, 10).map((card, index) => (
                  <React.Fragment key={card.id}>
                    <div className="w-[80px] rounded border border-slate-200 bg-white px-1.5 py-1.5 text-center dark:border-slate-700 dark:bg-slate-900">
                      <p className="line-clamp-2 text-[9px] font-medium text-slate-700 dark:text-slate-200">
                        {card.atividade || card.nome_fluxo || `Etapa ${index + 1}`}
                      </p>
                      <p className="mt-0.5 font-mono text-[8px] text-slate-500">
                        {toMinutes(card.tp ?? card.lt).toFixed(0)}m
                      </p>
                    </div>
                    {index < Math.min(cards.length, 10) - 1 && <span className="text-[9px] text-slate-400">→</span>}
                  </React.Fragment>
                ))}
              </div>
            </div>
            <div className="mt-1.5 flex items-center gap-2">
              <button type="button" onClick={onOpenVsm} className={secondaryButtonClassName()}>
                <ExternalLink size={12} />
                Abrir Canvas VSM
              </button>
              <span className="text-[9px] text-slate-500">{cards.length} etapas</span>
            </div>
          </SectionPanel>
        </div>

        <div className="flex flex-col gap-2 lg:col-span-5">
          <SectionPanel title="Gargalos" icon={<Timer size={12} className="text-red-500" />}>
            {bottlenecks.length === 0 ? (
              <p className="text-[10px] text-slate-500">Sem gargalos críticos no fluxo atual.</p>
            ) : (
              <ul className="space-y-1">
                {bottlenecks.map(({ card, wait }) => (
                  <li key={card.id} className="flex items-center justify-between text-[10px]">
                    <span className="truncate text-slate-700 dark:text-slate-200">{card.atividade || card.nome_fluxo}</span>
                    <span className="font-mono text-red-600 dark:text-red-400">{wait.toFixed(0)}m</span>
                  </li>
                ))}
              </ul>
            )}
          </SectionPanel>
          <div className="grid grid-cols-2 gap-1.5">
            <ClickableMetric label="Problemas" value={summary.issues_total} tone={summary.issues_critical > 0 ? 'warning' : 'default'} onClick={onNavigateCriticalProblems} />
            <ClickableMetric label="Ações" value={summary.actions_total} onClick={onNavigateActions} />
            <ClickableMetric label="Execuções" value={summary.executions_active} onClick={onNavigateExecutions} />
            <ClickableMetric label="Executores" value={executors} onClick={onNavigateActions} />
          </div>
        </div>
      </div>

      {(summary.actions_overdue > 0 || summary.issues_critical > 0) && (
        <div className="flex flex-wrap gap-1.5 text-[10px]">
          {summary.actions_overdue > 0 && (
            <button type="button" onClick={onNavigateOverdueActions} className="inline-flex items-center gap-1 rounded bg-red-50 px-2 py-0.5 text-red-700 dark:bg-red-950/30 dark:text-red-400">
              <AlertTriangle size={10} />
              {summary.actions_overdue} atrasadas
            </button>
          )}
          {summary.issues_critical > 0 && (
            <button type="button" onClick={onNavigateCriticalProblems} className="inline-flex items-center gap-1 rounded bg-amber-50 px-2 py-0.5 text-amber-700 dark:bg-amber-950/30 dark:text-amber-400">
              <Flame size={10} />
              {summary.issues_critical} críticos
            </button>
          )}
        </div>
      )}
    </div>
  );
};
